import React, { useMemo, useState } from 'react'
import { StringParam, useQueryParam, useQueryParams } from 'use-query-params'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useNotification, usePaginationQueryParams, useTableSearch, useTableSetting } from '@app/hooks'

import { ContentLayout } from '@app/layouts'
import { Header, ExtendedTable, SubheaderToolbarDivider, TableSettingsToolbar } from '@app/components'
import { LntButton } from '@app/components/base'
import { Show } from '@app/components/util'
import { InternalTaskCardDrawer } from '@app/drawers'

import { objectHasEveryNullableProperties } from '@app/utils/object'
import { getInternalTasks, patchInternalTasksItemExecutorSelf } from '@app/api/routes/internalTasks'

import { BUTTON_TYPE, SIZE } from '@app/constants'
import { INTERNAL_TASKS_KEY } from '@app/constants/queryKeys'
import { TABLE_SETTING_KEYS } from '@app/constants/tableSettingKeys'
import { QUERY_PARAMS_KEYS } from '@app/api/constants'

import { TopFilters } from './ui'
import { getColumns, adaptInternalTasksTableData } from './lib'
import { columnsSearchSchema, tableSearchQuerySchema, topFiltersQuerySchema } from './configs'

import type { ReactNode } from 'react'
import type { FiltersParams } from '@app/types/filter'
import type { GetInternalTasksParams } from '@app/api/routes/internalTasks/types'

const title = 'Список задач'

const columns = getColumns()

export const InternalTasksPage: React.FC = () => {
  const { paginationParams, pagination, setPagination, resetCurrentPage } = usePaginationQueryParams()
  const [topFiltersQuery, setTopFiltersQuery] = useQueryParams(topFiltersQuerySchema)

  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [queryTaskId, setQueryTaskId] = useQueryParam(QUERY_PARAMS_KEYS.id, StringParam)

  const queryClient = useQueryClient()
  const notification = useNotification()

  const tasksDataParams = useMemo(
    () => ({ ...paginationParams, ...topFiltersQuery }),
    [paginationParams, topFiltersQuery]
  ) as GetInternalTasksParams

  const { data: internalTasksTableData, isLoading: isTasksDataLoading } = useQuery({
    enabled: !objectHasEveryNullableProperties(topFiltersQuery),
    queryKey: [INTERNAL_TASKS_KEY.internalTasksItems, tasksDataParams],
    queryFn: async () => await getInternalTasks(tasksDataParams),
    select: internalTasksData => adaptInternalTasksTableData(internalTasksData)
  })

  const patchInternalTasksItemExecutorSelfMutation = useMutation({
    mutationFn: patchInternalTasksItemExecutorSelf,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [INTERNAL_TASKS_KEY.internalTasksItem] })

      notification.success({
        message: 'Задача успешно взята в работу.'
      })

      setQueryTaskId(null)
    }
  })

  const handlePaginationChange = (current: number, pageSize: number): void => {
    setPagination({ current, pageSize })
  }

  const handleFiltersChange = (filters: FiltersParams): void => {
    resetCurrentPage()
    setTopFiltersQuery(filters)
  }

  const handleSelectedRowChange = (selectedKeys: React.Key[]): void => {
    setSelectedIds(selectedKeys.map(String))
  }

  const handleChangeQueryTaskId = (key: string | null): void => {
    setQueryTaskId(key)
  }

  const handleTasksItemSelf = (): void => {
    if (!queryTaskId) return

    patchInternalTasksItemExecutorSelfMutation.mutate({ taskId: queryTaskId })
  }

  const {
    items,
    defaultItems,
    columns: modifiedColumns,
    onModify
  } = useTableSetting(TABLE_SETTING_KEYS.internalTasksPage, columns)

  const {
    columns: columnsWithSearchTool,
    filteredDataSource: filteredTasksDataSource,
    isSearchToolOpened,
    toggleSearchTool
  } = useTableSearch({
    columns: modifiedColumns,
    dataSource: internalTasksTableData?.dataSource,
    columnsSearchSchema,
    tableSearchQuerySchema
  })

  const toolbar: ReactNode[] = [
    <Show
      key='view_tasks-show'
      condition={ selectedIds.length === 1 }
    >
      <LntButton
        type={ BUTTON_TYPE.text }
        size={ SIZE.small }
        onClick={ () => handleChangeQueryTaskId(selectedIds[0]) }
      >
        Открыть
      </LntButton>

      <LntButton
        type={ BUTTON_TYPE.text }
        size={ SIZE.small }
        onClick={ handleTasksItemSelf }
      >
        Взять в работу
      </LntButton>

      <SubheaderToolbarDivider />
    </Show>,

    <TableSettingsToolbar
      key='table-settings-toolbar'
      tableSettingButtonProps={ { items, defaultItems, onAccept: onModify } }
      tableSearchButtonProps={ { active: isSearchToolOpened, onClick: toggleSearchTool } }
    />
  ]

  return (
    <>
      <Header controlProps={ { title } } />

      <ContentLayout>
        <TopFilters
          onSearch={ handleFiltersChange }
          initialFilters={ topFiltersQuery as FiltersParams }
        />

        <ExtendedTable
          loading={ isTasksDataLoading }
          columns={ columnsWithSearchTool }
          dataSource={ filteredTasksDataSource }
          toolbar={ toolbar }
          onRowDoubleClick={ (_, record) => handleChangeQueryTaskId(record.key) }
          rowSelection={ {
            onChange: handleSelectedRowChange
          } }
          pagination={ {
            ...pagination,
            total: internalTasksTableData?.count,
            onChange: handlePaginationChange
          } }
        />
      </ContentLayout>

      <InternalTaskCardDrawer
        open={ !!queryTaskId }
        taskId={ queryTaskId }
        onClose={ () => handleChangeQueryTaskId(null) }
        onSelf={ handleTasksItemSelf }
      />
    </>
  )
}
